import type {ReactNode} from 'react'
import {Button} from '@/components/ui/button'
import {DialogFooter} from '@/components/ui/dialog'
import {useI18n} from '@/i18n'

interface SaveCancelFooterProps {
    onCancel: () => void
    saving?: boolean
    disabled?: boolean
    saveLabel?: string
    savingLabel?: string
    cancelLabel?: string
    destructive?: boolean
    testIdPrefix: string
    saveType?: 'submit' | 'button'
    onSave?: () => void
    /**
     * Optional content rendered left-aligned, before the Cancel/Save pair.
     * Use for destructive auxiliary actions that belong with the dialog's
     * primary controls (e.g. "Clear Configuration").
     */
    leadingAction?: ReactNode
}

export function SaveCancelFooter({
    onCancel,
    saving = false,
    disabled = false,
    saveLabel,
    savingLabel,
    cancelLabel,
    destructive = false,
    testIdPrefix,
    saveType = 'submit',
    onSave,
    leadingAction,
}: SaveCancelFooterProps) {
    const i18n = useI18n()

    const resolvedCancelLabel = cancelLabel ?? i18n.common.cancel
    const resolvedSaveLabel = saveLabel ?? i18n.common.save
    const resolvedSavingLabel = savingLabel ?? i18n.common.saving

    return (
        <DialogFooter>
            {leadingAction && <div className="mr-auto">{leadingAction}</div>}
            <Button
                data-testid={`btn-cancel-${testIdPrefix}`}
                type="button"
                variant="outline"
                onClick={onCancel}
            >
                {resolvedCancelLabel}
            </Button>
            <Button
                data-testid={`btn-save-${testIdPrefix}`}
                type={saveType}
                variant={destructive ? 'destructive' : 'default'}
                disabled={saving || disabled}
                onClick={saveType === 'button' ? onSave : undefined}
            >
                {saving ? resolvedSavingLabel : resolvedSaveLabel}
            </Button>
        </DialogFooter>
    )
}
